/**
 * command-view.tsx — W66.X7 COCKPIT pattern
 *
 * Bryan K. Short doctrine, 2026-06-03:
 *   "Cockpit framing — wayfinding to dedicated full-screen surfaces. Each tile is a large
 *    graphical thumbnail; click takes you to the full view. Show pack percentile vs the rest
 *    of the team. Today stays separate. Don't try to render everything on one page."
 *
 * Routes:
 *   /command-view              — Cockpit tile grid (NEW — replaces cramped Command View)
 *   /account/:id/command-view  — Per-account deep-link (AccountCommandViewPage, unchanged)
 *
 * Layout: Desktop 3×2, iPad 2×3, Mobile 1×6.
 * Six tiles: Position, Tower, Helix DNA, Galaxy, Activity Pulse, Coach Prompt.
 *
 * W66.X6 panel components (HelixPanel, TowerPanel, GalaxyPanel) are preserved in
 * client/src/components/command-view/ but are no longer imported here.
 * They can be removed in a later wave once cockpit ships clean.
 *
 * Canon:
 *   No exclamation points. No emojis. M-PACT hyphenated.
 *   "Checkpoint" not "Gate". CP format: "CP{N} — {Name}".
 *   5-color palette: #6B7280, #DC2626, #EA580C, #EAB308, #16A34A.
 *   Tenant 1 only. Bryan K. Short. Demo AE 1 (never Geoff).
 */

import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import CommandViewLayout from "@/components/command-view/CommandViewLayout";
import { api, getSession } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";

// ── Brand constants ────────────────────────────────────────────────────────────
const BG        = "#F9FAFB";
const CARD      = "#FFFFFF";
const BORDER    = "#E5E7EB";
const BORDER_HOVER = "#DC2626";
const MI6_RED   = "#DC2626";
const ORANGE    = "#EA580C";
const YELLOW    = "#EAB308";
const GREEN     = "#16A34A";
const GRAY      = "#6B7280";
const TEXT_DARK = "#111827";
const TEXT_MID  = "#374151";
const TEXT_MUTE = "#6B7280";

// ── FLOOR_Y_PCT — locked mapping (detrended warm-signal detection, W66.X7) ────
const FLOOR_Y_PCT: Record<number, number> = {
  1: 58.76, 2: 53.71, 3: 48.71, 4: 43.71, 5: 38.71,
  6: 33.77, 7: 28.83, 8: 23.89, 9: 18.95, 10: 14.06,
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt$(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function pctTo(closed: number, quota: number): string {
  if (!quota) return "—";
  return `${Math.round((closed / quota) * 100)}%`;
}

function coverageColor(ratio: number): string {
  if (ratio >= 5) return GREEN;
  if (ratio >= 3) return YELLOW;
  return MI6_RED;
}

function orbColor(score: number | null | undefined): string {
  if (score === null || score === undefined || score === 0) return GRAY;
  if (score < 25)  return MI6_RED;
  if (score < 50)  return ORANGE;
  if (score < 75)  return YELLOW;
  return GREEN;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface GapMathData {
  has_quota: boolean;
  annual_quota: number;
  closed_ytd: number;
  gap: number;
  weighted_pipeline: number;
  coverage_ratio: number;
  coach_cue: string;
}

interface Top10Account {
  rank: number;
  account_id: number;
  account_name: string;
  composite_score: number;
  next_cp: number | null;
  next_cp_label: string | null;
  days_stalled: number | null;
  forecast_bucket: string;
}

interface W50Payload {
  top_10_accounts: Top10Account[];
  cadence_due_v2: unknown[];
  yesterday_wins: unknown[];
  meta: { today: string; role: string };
}

interface PackStanding {
  percentile: number;
  label: string;
  labelColor: string;
}

// ── Pack percentile computation ────────────────────────────────────────────────
// Computes current user's percentile vs all AE users in tenant 1 based on
// (closed_ytd + 0.4 × weighted_pipeline). Fetched from /api/today/gap-math
// which only returns the current user's own data. We approximate rank from
// the coach_cue field or fall back to a tenant-wide comparison via /api/team/position.
// For now: if we can't get team data we show "—" rather than phantom data.
function computePackLabel(percentile: number): { label: string; color: string } {
  if (percentile >= 80) return { label: "Top 20% on the team", color: GREEN };
  if (percentile >= 40) return { label: "Middle of pack", color: YELLOW };
  return { label: `Below pack — ${percentile}th percentile`, color: MI6_RED };
}

// ── Tile card base ────────────────────────────────────────────────────────────
interface TileProps {
  title: string;
  caption?: string;
  onClick: () => void;
  children: React.ReactNode;
  minHeight?: number;
}

function CockpitTile({ title, caption, onClick, children, minHeight = 280 }: TileProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: CARD,
        border: `1px solid ${hovered ? BORDER_HOVER : BORDER}`,
        borderRadius: 12,
        padding: 16,
        minHeight,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: hovered
          ? "0 4px 16px rgba(220,38,38,0.10), 0 1px 3px rgba(0,0,0,0.08)"
          : "0 1px 3px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-2px)" : "none",
        transition: "all 0.15s ease",
      }}
    >
      {/* Tile eyebrow */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: TEXT_MUTE,
        }}
      >
        {title}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        {children}
      </div>

      {/* Caption */}
      {caption && (
        <div style={{ fontSize: 11, color: TEXT_MUTE, borderTop: `1px solid ${BORDER}`, paddingTop: 8, marginTop: 4 }}>
          {caption}
        </div>
      )}
    </div>
  );
}

// ── Tile 1 — POSITION & PACK STANDING ────────────────────────────────────────
function PositionTile({
  gapMath,
  packStanding,
  gapLoading,
  onClick,
}: {
  gapMath: GapMathData | null;
  packStanding: PackStanding | null;
  gapLoading: boolean;
  onClick: () => void;
}) {
  const annual   = gapMath?.annual_quota ?? 0;
  const closed   = gapMath?.closed_ytd ?? 0;
  const coverage = gapMath?.coverage_ratio ?? 0;
  const quotaPct = annual > 0 ? Math.round((closed / annual) * 100) : null;

  return (
    <CockpitTile
      title="Your number"
      onClick={onClick}
    >
      {gapLoading ? (
        <div className="animate-pulse" style={{ background: BORDER, borderRadius: 4, height: 48, width: "60%" }} />
      ) : !gapMath?.has_quota ? (
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>No quota on file.</p>
      ) : (
        <>
          {/* Big metric */}
          <div style={{ fontSize: 48, fontWeight: 800, color: TEXT_DARK, lineHeight: 1.0, marginBottom: 4 }}>
            {quotaPct !== null ? `${quotaPct}%` : "—"}
          </div>
          <div style={{ fontSize: 12, color: TEXT_MUTE, marginBottom: 12 }}>
            to annual quota &mdash; {fmt$(closed)} of {fmt$(annual)}
          </div>

          {/* Coverage sub-row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: coverageColor(coverage),
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            <span>{coverage.toFixed(1)}x pipeline coverage</span>
            <span style={{ fontSize: 11, color: TEXT_MUTE, fontWeight: 400 }}>
              &middot; rule of thumb 5x
            </span>
          </div>

          {/* Pack percentile chip */}
          {packStanding ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: `${packStanding.labelColor}18`,
                border: `1px solid ${packStanding.labelColor}40`,
                borderRadius: 20,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 600,
                color: packStanding.labelColor,
              }}
            >
              {packStanding.label}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: TEXT_MUTE }}>Pack standing unavailable</div>
          )}
        </>
      )}
    </CockpitTile>
  );
}

// ── Tile 2 — TOWER (thumbnail) ────────────────────────────────────────────────
function TowerTile({
  accounts,
  loading,
  onClick,
}: {
  accounts: Top10Account[];
  loading: boolean;
  onClick: () => void;
}) {
  // Determine the majority/average floor from accounts' next_cp
  const cpFloors = accounts
    .map((a) => a.next_cp)
    .filter((cp): cp is number => cp !== null && cp >= 1 && cp <= 10);

  const activeFloors = Array.from(new Set(cpFloors));
  const avgFloor = cpFloors.length
    ? Math.round(cpFloors.reduce((s, v) => s + v, 0) / cpFloors.length)
    : null;

  // Majority floor
  const floorCounts: Record<number, number> = {};
  cpFloors.forEach((f) => { floorCounts[f] = (floorCounts[f] ?? 0) + 1; });
  const majorityFloor = activeFloors.reduce(
    (best, f) => (floorCounts[f] > (floorCounts[best] ?? 0) ? f : best),
    activeFloors[0] ?? avgFloor ?? 5
  );

  const activePipFloor = majorityFloor ?? avgFloor ?? null;

  const caption = loading
    ? "Loading..."
    : `${activeFloors.length} of 10 floors active across ${accounts.length} opportunities`;

  return (
    <CockpitTile title="Pipeline Tower" caption={caption} onClick={onClick}>
      <div style={{ position: "relative", width: "100%", height: 200 }}>
        {/* Tower image */}
        <img
          src="/hero/mi6-tower-clean-desktop.webp"
          alt="Pipeline Tower"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center top",
            display: "block",
          }}
        />

        {/* Orb overlays */}
        {!loading && activePipFloor !== null && (() => {
          const containerH = 200;
          return activeFloors.map((floor) => {
            const yPct = FLOOR_Y_PCT[floor];
            if (!yPct) return null;
            const isPrimary = floor === activePipFloor;
            const top = (yPct / 100) * containerH;

            return (
              <div
                key={floor}
                style={{
                  position: "absolute",
                  left: "50%",
                  top,
                  transform: "translate(-50%, -50%)",
                  width: isPrimary ? 12 : 7,
                  height: isPrimary ? 12 : 7,
                  borderRadius: "50%",
                  background: isPrimary ? MI6_RED : GREEN,
                  border: `2px solid ${CARD}`,
                  boxShadow: isPrimary
                    ? `0 0 8px 2px ${MI6_RED}80`
                    : undefined,
                  animation: isPrimary ? "cockpit-pulse 1.5s ease-in-out infinite" : undefined,
                  zIndex: 10,
                }}
              />
            );
          });
        })()}
      </div>

      <style>{`
        @keyframes cockpit-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px 2px rgba(220,38,38,0.5); }
          50% { opacity: 0.7; box-shadow: 0 0 14px 4px rgba(220,38,38,0.8); }
        }
      `}</style>
    </CockpitTile>
  );
}

// ── Tile 3 — HELIX DNA (aggregate orb strip) ──────────────────────────────────
function HelixTile({
  accounts,
  loading,
  onClick,
}: {
  accounts: Top10Account[];
  loading: boolean;
  onClick: () => void;
}) {
  // Aggregate per-CP average score across all accounts
  // composite_score represents overall deal health; distribute across 10 CP slots
  const orbColors = accounts.slice(0, 10).map((a) => orbColor(a.composite_score));

  // Fill to 10 orbs
  while (orbColors.length < 10) orbColors.push(GRAY);

  const strongCount = accounts.filter((a) => a.composite_score >= 75).length;

  const caption = loading
    ? "Loading..."
    : `${accounts.length} opportunities. ${strongCount} with strong scoring on 5+ CPs.`;

  return (
    <CockpitTile title="Opportunity DNA" caption={caption} onClick={onClick}>
      {loading ? (
        <div className="animate-pulse" style={{ background: BORDER, borderRadius: 4, height: 40, width: "80%" }} />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {orbColors.map((color, i) => (
            <div
              key={i}
              title={`CP${i + 1}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: color,
                border: `2px solid ${CARD}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                flexShrink: 0,
              }}
            />
          ))}
        </div>
      )}

      {/* Color legend */}
      {!loading && (
        <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
          {[
            { color: GREEN,   label: "Strong" },
            { color: YELLOW,  label: "Qualified" },
            { color: ORANGE,  label: "Developing" },
            { color: MI6_RED, label: "Early" },
            { color: GRAY,    label: "Unanswered" },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: TEXT_MUTE }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
              {label}
            </div>
          ))}
        </div>
      )}
    </CockpitTile>
  );
}

// ── Tile 4 — GALAXY / ACCOUNT PATHS ──────────────────────────────────────────
function GalaxyTile({
  accounts,
  loading,
  onClick,
}: {
  accounts: Top10Account[];
  loading: boolean;
  onClick: () => void;
}) {
  // Fallback styled illustration: decision tree nodes
  const inMotion = accounts.filter((a) => a.next_cp !== null).length;

  const caption = loading
    ? "Loading..."
    : `${inMotion} branches in motion across ${accounts.length} opportunities.`;

  return (
    <CockpitTile title="Account Paths" caption={caption} onClick={onClick}>
      {/* Compact SVG decision tree illustration */}
      <svg
        viewBox="0 0 200 120"
        style={{ width: "100%", height: 120, display: "block" }}
        aria-hidden="true"
      >
        {/* Root node */}
        <rect x="80" y="8" width="40" height="20" rx="4" fill="#111827" />
        <text x="100" y="22" textAnchor="middle" fontSize="9" fill="#FFFFFF" fontFamily="Inter,sans-serif">START</text>

        {/* Branch lines */}
        <line x1="100" y1="28" x2="60" y2="50" stroke={BORDER} strokeWidth="1.5" />
        <line x1="100" y1="28" x2="140" y2="50" stroke={BORDER} strokeWidth="1.5" />

        {/* Left branch node */}
        <rect x="30" y="50" width="60" height="18" rx="3" fill={MI6_RED} />
        <text x="60" y="63" textAnchor="middle" fontSize="8" fill="#FFFFFF" fontFamily="Inter,sans-serif">Qualify</text>

        {/* Right branch node */}
        <rect x="110" y="50" width="60" height="18" rx="3" fill={GREEN} />
        <text x="140" y="63" textAnchor="middle" fontSize="8" fill="#FFFFFF" fontFamily="Inter,sans-serif">Advance</text>

        {/* Left sub-branches */}
        <line x1="60" y1="68" x2="40" y2="88" stroke={BORDER} strokeWidth="1" />
        <line x1="60" y1="68" x2="80" y2="88" stroke={BORDER} strokeWidth="1" />
        <diamond x="30" y="88" />
        <polygon points="40,88 46,96 40,104 34,96" fill={YELLOW} />
        <rect x="68" y="88" width="24" height="14" rx="3" fill={GRAY} />
        <text x="80" y="98" textAnchor="middle" fontSize="7" fill="#FFFFFF" fontFamily="Inter,sans-serif">Hold</text>

        {/* Right sub-branch */}
        <line x1="140" y1="68" x2="140" y2="88" stroke={BORDER} strokeWidth="1" />
        <rect x="118" y="88" width="44" height="14" rx="3" fill={ORANGE} />
        <text x="140" y="98" textAnchor="middle" fontSize="7" fill="#FFFFFF" fontFamily="Inter,sans-serif">Budget check</text>
      </svg>
    </CockpitTile>
  );
}

// ── Tile 5 — ACTIVITY PULSE ───────────────────────────────────────────────────
interface ActivityWeekData {
  days: { label: string; count: number }[];
  totalThisWeek: number;
  totalLastWeek: number;
  daysActive: number;
  accountsTouched: number;
}

function ActivityTile({
  activityData,
  activityLoading,
  onClick,
}: {
  activityData: ActivityWeekData | null;
  activityLoading: boolean;
  onClick: () => void;
}) {
  const days = activityData?.days ?? [];
  const maxVal = Math.max(...days.map((d) => d.count), 1);

  const thisWeek = activityData?.totalThisWeek ?? 0;
  const lastWeek = activityData?.totalLastWeek ?? 0;
  const diff = thisWeek - lastWeek;
  const daysActive = activityData?.daysActive ?? 0;
  const accountsTouched = activityData?.accountsTouched ?? 0;

  const caption = activityLoading
    ? "Loading..."
    : `${thisWeek} writes this week (${diff >= 0 ? "+" : ""}${diff} vs last). ${daysActive} of 7 days active. ${accountsTouched} accounts touched.`;

  const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <CockpitTile title="Activity this week" caption={caption} onClick={onClick}>
      {activityLoading ? (
        <div className="animate-pulse" style={{ background: BORDER, borderRadius: 4, height: 60, width: "100%" }} />
      ) : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 64, marginTop: 8 }}>
          {DAY_LABELS.map((label, i) => {
            const val = days[i]?.count ?? 0;
            const barH = maxVal > 0 ? Math.max(4, Math.round((val / maxVal) * 52)) : 4;
            return (
              <div
                key={i}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
              >
                <div
                  style={{
                    width: "100%",
                    height: barH,
                    background: MI6_RED,
                    borderRadius: "2px 2px 0 0",
                    opacity: val === 0 ? 0.2 : 1,
                    alignSelf: "flex-end",
                  }}
                />
                <div style={{ fontSize: 9, color: TEXT_MUTE }}>{label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sub-metrics */}
      {!activityLoading && (
        <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEXT_DARK }}>{thisWeek}</div>
          <div style={{ fontSize: 12, color: TEXT_MUTE, alignSelf: "center" }}>writes this week</div>
        </div>
      )}
    </CockpitTile>
  );
}

// ── Tile 6 — COACH PROMPT ────────────────────────────────────────────────────
function deriveCoachPrompt(accounts: Top10Account[]): { text: string; accountId: number | null } {
  // Stalled > 14 days
  const stalled = accounts.filter((a) => (a.days_stalled ?? 0) > 14);
  if (stalled.length > 0) {
    const a = stalled[0];
    return {
      text: `${a.account_name} — at CP${a.next_cp ?? "?"} for ${a.days_stalled} days with no recent evidence. What is your next move?`,
      accountId: a.account_id,
    };
  }

  // Stalled > 21 days
  const deepStalled = accounts.filter((a) => (a.days_stalled ?? 0) > 21);
  if (deepStalled.length > 0) {
    const a = deepStalled[0];
    return {
      text: `${a.account_name} — stalled for over three weeks. Push, hold, or bow out?`,
      accountId: a.account_id,
    };
  }

  // CP5+ without strong scoring
  const highCp = accounts.filter((a) => (a.next_cp ?? 0) >= 5 && (a.composite_score ?? 0) < 60);
  if (highCp.length >= 3) {
    return {
      text: `Three accounts at CP5 or above without strategic fit confirmed. Which one gets your energy this week?`,
      accountId: null,
    };
  }

  // Quiet book
  return {
    text: "Quiet book. Use the time to expand contact coverage on your Top-10.",
    accountId: null,
  };
}

function CoachTile({
  accounts,
  loading,
  onClick,
}: {
  accounts: Top10Account[];
  loading: boolean;
  onClick: () => void;
}) {
  const prompt = loading ? null : deriveCoachPrompt(accounts);

  return (
    <CockpitTile title="Today's question" onClick={onClick}>
      {loading ? (
        <div className="animate-pulse" style={{ background: BORDER, borderRadius: 4, height: 60, width: "90%" }} />
      ) : (
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: TEXT_DARK,
            lineHeight: 1.5,
            marginTop: 8,
            fontStyle: "italic",
          }}
        >
          "{prompt?.text}"
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: TEXT_MUTE }}>
        Open account to respond
      </div>
    </CockpitTile>
  );
}

// ── Cockpit date helpers ───────────────────────────────────────────────────────
function isoWeekDates(offset = 0): { from: string; to: string } {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(monday), to: fmt(sunday) };
}

// ── Cockpit main layout ────────────────────────────────────────────────────────
function CockpitLayout() {
  const [, navigate] = useLocation();
  const session = getSession();
  const repName = (session as any)?.name ?? "";
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const [gapMath, setGapMath] = useState<GapMathData | null>(null);
  const [gapLoading, setGapLoading] = useState(true);
  const [payload, setPayload] = useState<W50Payload | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(true);
  const [activityData, setActivityData] = useState<ActivityWeekData | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [packStanding, setPackStanding] = useState<PackStanding | null>(null);

  useEffect(() => {
    // Gap math (position data)
    api<GapMathData>("/api/today/gap-math")
      .then((d) => setGapMath(d))
      .catch(() => setGapMath(null))
      .finally(() => setGapLoading(false));

    // Today payload (Top-10 accounts for coach prompts + helix)
    api<W50Payload>("/api/today/w40")
      .then((d) => setPayload(d))
      .catch(() => setPayload(null))
      .finally(() => setPayloadLoading(false));

    // Activity — current week + last week
    const thisWeek = isoWeekDates(0);
    const lastWeek = isoWeekDates(-1);

    Promise.all([
      api<{ rows: any[] }>(`/api/me/activity?from=${thisWeek.from}&to=${thisWeek.to}`).catch(() => ({ rows: [] })),
      api<{ rows: any[] }>(`/api/me/activity?from=${lastWeek.from}&to=${lastWeek.to}`).catch(() => ({ rows: [] })),
    ]).then(([thisData, lastData]) => {
      const thisRows = thisData?.rows ?? [];
      const lastRows = lastData?.rows ?? [];

      // Build 7-day bar (Mon–Sun this week)
      const dayMap: Record<string, number> = {};
      thisRows.forEach((r: any) => {
        const date = r.activity_date ?? "";
        const writes = Number(r.outreach ?? 0) + Number(r.first_meetings ?? 0) + Number(r.qualified_meetings ?? 0);
        dayMap[date] = (dayMap[date] ?? 0) + writes;
      });

      const days: { label: string; count: number }[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(thisWeek.from);
        d.setDate(d.getDate() + i);
        const key = d.toISOString().slice(0, 10);
        days.push({ label: ["M","T","W","T","F","S","S"][i], count: dayMap[key] ?? 0 });
      }

      const totalThisWeek = days.reduce((s, d) => s + d.count, 0);
      const totalLastWeek = lastRows.reduce((s: number, r: any) =>
        s + Number(r.outreach ?? 0) + Number(r.first_meetings ?? 0) + Number(r.qualified_meetings ?? 0), 0);
      const daysActive = days.filter((d) => d.count > 0).length;
      const accountsTouched = new Set(thisRows.map((r: any) => r.account_id).filter(Boolean)).size;

      setActivityData({ days, totalThisWeek, totalLastWeek, daysActive, accountsTouched });
    }).finally(() => setActivityLoading(false));
  }, []);

  // Pack standing: derive from gap-math once loaded
  // Approximation: we don't have team-wide data here, so we surface the coach_cue
  // hint when available. If /api/team/position endpoint exists, use it; otherwise skip.
  useEffect(() => {
    if (!gapMath) return;
    api<{ rank: number; total: number }>("/api/team/position")
      .then((data) => {
        if (data?.rank && data?.total) {
          const percentile = Math.round(((data.total - data.rank) / data.total) * 100);
          const { label, color } = computePackLabel(percentile);
          setPackStanding({ percentile, label, labelColor: color });
        }
      })
      .catch(() => {
        // Endpoint not available — show nothing rather than phantom data
        setPackStanding(null);
      });
  }, [gapMath]);

  const accounts = payload?.top_10_accounts ?? [];

  return (
    <OperatorShell breadcrumbs={[]}>
      <div style={{ background: BG, minHeight: "100vh" }}>

        {/* Page header */}
        <header
          style={{
            background: CARD,
            borderBottom: `1px solid ${BORDER}`,
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: MI6_RED,
              }}
            >
              COCKPIT
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK, lineHeight: 1.2 }}>
              {repName || "Command View"}
            </div>
          </div>
          <div style={{ fontSize: 12, color: TEXT_MUTE }}>{today}</div>
        </header>

        {/* Tile grid */}
        <div
          style={{
            padding: "24px 20px",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gap: 16,
              // Desktop: 3 columns. iPad: 2 columns. Mobile: 1 column.
              gridTemplateColumns: "repeat(3, 1fr)",
            }}
            className="cockpit-grid"
          >
            {/* Tile 1 — Position */}
            <PositionTile
              gapMath={gapMath}
              packStanding={packStanding}
              gapLoading={gapLoading}
              onClick={() => navigate("/forecast")}
            />

            {/* Tile 2 — Tower */}
            <TowerTile
              accounts={accounts}
              loading={payloadLoading}
              onClick={() => navigate("/tower")}
            />

            {/* Tile 3 — Helix DNA */}
            <HelixTile
              accounts={accounts}
              loading={payloadLoading}
              onClick={() => navigate("/opportunity-dna")}
            />

            {/* Tile 4 — Galaxy */}
            <GalaxyTile
              accounts={accounts}
              loading={payloadLoading}
              onClick={() => navigate("/galaxy")}
            />

            {/* Tile 5 — Activity Pulse */}
            <ActivityTile
              activityData={activityData}
              activityLoading={activityLoading}
              onClick={() => navigate("/activity-audit")}
            />

            {/* Tile 6 — Coach Prompt */}
            <CoachTile
              accounts={accounts}
              loading={payloadLoading}
              onClick={() => {
                const prompt = deriveCoachPrompt(accounts);
                if (prompt.accountId) {
                  navigate(`/accounts/${prompt.accountId}`);
                } else {
                  navigate("/today");
                }
              }}
            />
          </div>

          {/* Footer note */}
          <p
            style={{
              textAlign: "center",
              fontSize: 11,
              color: TEXT_MUTE,
              marginTop: 24,
            }}
          >
            Each tile opens a full view.
          </p>
        </div>

        {/* Responsive grid styles */}
        <style>{`
          @media (max-width: 1023px) and (min-width: 640px) {
            .cockpit-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          @media (max-width: 639px) {
            .cockpit-grid {
              grid-template-columns: 1fr !important;
            }
          }
          @media (min-width: 640px) and (max-width: 1023px) {
            .cockpit-tile-min { min-height: 220px !important; }
          }
        `}</style>
      </div>
    </OperatorShell>
  );
}

// ── Per-account Command View (unchanged — /account/:id/command-view) ───────────
export function AccountCommandViewPage() {
  const params = useParams<{ id: string }>();
  const accountId = params.id ?? null;

  if (!accountId) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: CARD,
          color: TEXT_MUTE,
          fontFamily: "Inter, system-ui, sans-serif",
          gap: 12,
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>No account ID in URL</div>
        <div style={{ fontSize: 12, color: TEXT_MUTE, maxWidth: 320 }}>
          Use a direct link like{" "}
          <span style={{ fontFamily: "monospace", color: TEXT_DARK }}>/account/38/command-view</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <CommandViewLayout accountId={String(accountId)} />
    </div>
  );
}

// ── Default export — /command-view → Cockpit ───────────────────────────────────
export default function CommandViewPage() {
  return <CockpitLayout />;
}
